import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import CheckoutForm from '../../components/CheckoutForm';
import env from '../../config/env';
import { useAxios } from '../../utils/hook/useAxios';

const stripePromise = loadStripe(env.stripePublicKey);

function RadioDot({ selected }) {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${selected ? '#0d6efd' : '#ccc'}`,
        backgroundColor: selected ? '#0d6efd' : 'white',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {selected && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'white',
          }}
        />
      )}
    </div>
  );
}

function CheckoutPage() {
  const location = useLocation();
  const axios = useAxios();

  /*
   * ============================================================
   * URL
   * ============================================================
   *
   * La seule donnée acceptée depuis l'URL est reservationId.
   *
   * Exemple :
   * /checkout?reservationId=200
   *
   * Aucun prix, date, véhicule, réduction ou durée
   * ne vient de l'URL.
   */

  const searchParams = new URLSearchParams(location.search);
  const reservationId = searchParams.get('reservationId');

  /*
   * ============================================================
   * RESERVATION
   * ============================================================
   */

  const [reservation, setReservation] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  /*
   * ============================================================
   * PAYMENT
   * ============================================================
   */

  const [clientSecret, setClientSecret] = useState('');
  const [step, setStep] = useState('plan');

  /*
   * ============================================================
   * INSURANCE
   * ============================================================
   */

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [insuranceFee, setInsuranceFee] = useState(0);

  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);

  /*
   * ============================================================
   * DELIVERY LOCATION
   * ============================================================
   */

  const [deliveryLocation, setDeliveryLocation] = useState(null);

  /*
   * ============================================================
   * CHARGEMENT RESERVATION
   * ============================================================
   */

  useEffect(() => {
    if (!reservationId) {
      setLoadError('Identifiant de réservation manquant.');
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadReservation = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const { data } = await axios.get(`/reservations/getbyid/${reservationId}`);

        if (!mounted) return;

        setReservation(data);

        /*
         * Si une assurance est déjà attachée à la réservation,
         * on initialise l'interface avec celle-ci.
         */
        if (data.insurancePlanId) {
          setSelectedPlanId(data.insurancePlanId);
          setInsuranceFee(Number(data.insuranceFee || 0));
        } else {
          setSelectedPlanId(null);
          setInsuranceFee(0);
        }
      } catch (error) {
        if (!mounted) return;

        console.error('Erreur récupération réservation :', error);

        setLoadError(error.response?.data?.message || 'Impossible de récupérer cette réservation.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadReservation();

    return () => {
      mounted = false;
    };
  }, [reservationId, axios]);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const response = await axios.post('/payments/intent', {
          reservationId,
        });

        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error('Erreur création PaymentIntent:', error.response?.data || error);
      }
    };

    if (reservationId) {
      createPaymentIntent();
    }
  }, [reservationId]);
  /*
   * ============================================================
   * CHARGEMENT DES PLANS D'ASSURANCE
   * ============================================================
   */

  useEffect(() => {
    let mounted = true;

    const loadInsurancePlans = async () => {
      try {
        const { data } = await axios.get('/insurance/plans');

        if (!mounted) return;

        setPlans(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erreur récupération plans assurance :', error);
      }
    };

    loadInsurancePlans();

    return () => {
      mounted = false;
    };
  }, [axios]);

  /*
   * ============================================================
   * DELIVERY LOCATION
   * ============================================================
   *
   * L'annonce est déjà fournie par la réservation.
   * On récupère uniquement les informations supplémentaires
   * de livraison si elles existent.
   */

  useEffect(() => {
    if (!reservation?.vehiculeAnnonceId) {
      return;
    }

    let mounted = true;

    const loadDeliveryLocation = async () => {
      try {
        const { data } = await axios.get(
          `/deliverylocation/getannoncebyid/${reservation.vehiculeAnnonceId}`
        );

        if (!mounted) return;

        setDeliveryLocation(Array.isArray(data) ? data[0] || null : data || null);
      } catch (error) {
        /*
         * Ce endpoint n'est pas indispensable au checkout.
         * L'annonce contient déjà locationAddress.
         */
        console.error('Erreur récupération lieu de livraison :', error);
      }
    };

    loadDeliveryLocation();

    return () => {
      mounted = false;
    };
  }, [reservation?.vehiculeAnnonceId, axios]);

  /*
   * ============================================================
   * DONNÉES DE LA RÉSERVATION
   * ============================================================
   */

  const vehicle = reservation?.Vehicle || null;

  const vehicleModel = vehicle?.VehicleModel || null;

  const host = reservation?.Host || null;

  const annonce = reservation?.VehiculeAnnonce || null;

  /*
   * ============================================================
   * DATES
   * ============================================================
   *
   * Elles viennent maintenant du backend.
   */

  const startDate = reservation?.startDate || null;
  const endDate = reservation?.endDate || null;

  const startMoment = useMemo(() => {
    return startDate ? moment(startDate) : null;
  }, [startDate]);

  const endMoment = useMemo(() => {
    return endDate ? moment(endDate) : null;
  }, [endDate]);

  /*
   * Durée pour l'affichage.
   *
   * IMPORTANT :
   * Cette valeur n'est jamais envoyée au backend.
   *
   * Le backend recalcule toujours la durée pour le prix réel.
   */

  const nombreJours = useMemo(() => {
    if (!startMoment || !endMoment) {
      return 0;
    }

    return Math.max(1, endMoment.diff(startMoment, 'days'));
  }, [startMoment, endMoment]);

  /*
   * ============================================================
   * PRIX DE L'ANNONCE
   * ============================================================
   *
   * reservationPrice est fourni par :
   *
   * reservation.VehiculeAnnonce.reservationPrice
   */

  const pricePerDay = Number(annonce?.reservationPrice || 0);

  /*
   * Pour l'affichage uniquement.
   *
   * Le montant réellement débité est calculé
   * côté backend dans /payments/intent.
   */

  const rentalSubtotal = pricePerDay * nombreJours;

  const displayedTotal = rentalSubtotal + Number(insuranceFee || 0);

  /*
   * Distance incluse.
   */

  const freeMinDistance = annonce?.minDistanceInclus ?? null;

  /*
   * ============================================================
   * ASSURANCE
   * ============================================================
   */

  const handleSelectPlan = async (planId) => {
    if (planLoading || !reservationId) {
      return;
    }

    try {
      setPlanLoading(true);
      setPlanError(null);

      /*
       * Le frontend envoie uniquement le choix du plan.
       *
       * Le backend récupère lui-même :
       * - startDate
       * - endDate
       * - reservation
       * - utilisateur
       *
       * et calcule la durée + le montant.
       */

      const { data } = await axios.post(`/insurance/reservations/${reservationId}/select`, {
        planId: planId === 'none' ? null : planId,
      });

      if (planId === 'none') {
        setSelectedPlanId('none');
        setInsuranceFee(0);
      } else {
        setSelectedPlanId(planId);
        setInsuranceFee(Number(data?.insuranceFee || 0));
      }

      /*
       * Si le backend renvoie le nouveau montant
       * d'assurance, on le prend comme source d'affichage.
       */
    } catch (error) {
      console.error('Erreur sélection assurance :', error);

      setPlanError(error.response?.data?.message || 'Erreur lors de la sélection du plan.');
    } finally {
      setPlanLoading(false);
    }
  };

  /*
   * ============================================================
   * PAYMENT INTENT
   * ============================================================
   */

  const handleContinueToPayment = async () => {
    if (!reservationId) {
      return;
    }

    try {
      setPlanLoading(true);
      setPlanError(null);

      /*
       * IMPORTANT :
       *
       * Aucun montant n'est envoyé.
       *
       * Le serveur récupère la réservation et calcule
       * le montant réel.
       */

      const { data } = await axios.post('/paiements/intent', {
        reservationId,
      });

      if (!data?.clientSecret) {
        throw new Error("Le serveur n'a pas retourné de clientSecret.");
      }

      setClientSecret(data.clientSecret);
      setStep('payment');
    } catch (error) {
      console.error('Erreur création PaymentIntent :', error);

      setPlanError(error.response?.data?.message || 'Impossible de préparer le paiement.');
    } finally {
      setPlanLoading(false);
    }
  };

  /*
   * ============================================================
   * STRIPE
   * ============================================================
   */

  const appearance = {
    theme: 'stripe',
  };

  const options = {
    clientSecret,
    appearance,
  };

  /*
   * ============================================================
   * CHARGEMENT
   * ============================================================
   */

  if (loading) {
    return (
      <main>
        <div className="container py-4">
          <div className="text-center py-5">
            <span className="spinner-border text-primary" />

            <p className="text-muted mt-3 mb-0">Chargement de votre réservation…</p>
          </div>
        </div>
      </main>
    );
  }

  /*
   * ============================================================
   * ERREUR
   * ============================================================
   */

  if (loadError || !reservation) {
    return (
      <main>
        <div className="container py-4">
          <div className="alert alert-danger">{loadError || 'Réservation introuvable.'}</div>
        </div>
      </main>
    );
  }

  /*
   * ============================================================
   * ANNULATION
   * ============================================================
   */

  const freeCancelDeadline = startMoment ? startMoment.clone().subtract(7, 'days') : null;

  const partialCancelDeadline = startMoment ? startMoment.clone().subtract(48, 'hours') : null;

  const now = moment();

  const isFreeCancel = freeCancelDeadline && now.isBefore(freeCancelDeadline);

  const isPartialCancel =
    !isFreeCancel && partialCancelDeadline && now.isBefore(partialCancelDeadline);

  /*
   * ============================================================
   * UI
   * ============================================================
   */

  return (
    <main>
      <div className="container py-4">
        <div className="row g-4">
          {/* ==================================================
              LEFT COLUMN
          ================================================== */}

          <div className="col-md-8">
            <div className="border rounded-3 p-4">
              <h1 className="mb-3">
                <strong>Checkout</strong>
              </h1>

              {/* ==================================================
                  CANCELLATION
              ================================================== */}

              {isFreeCancel ? (
                <div className="alert alert-success d-flex gap-2" role="alert">
                  <i className="bi bi-patch-check-fill text-success flex-shrink-0 mt-1" />

                  <div>
                    <p className="mb-1 fw-semibold">
                      Annulation gratuite avant le {freeCancelDeadline.format('DD MMM YYYY')}.
                    </p>

                    <p className="text-muted small mb-0">
                      Annulez avant cette date pour être remboursé intégralement.
                    </p>
                  </div>
                </div>
              ) : isPartialCancel ? (
                <div className="alert alert-warning d-flex gap-2" role="alert">
                  <i className="bi bi-exclamation-triangle-fill text-warning flex-shrink-0 mt-1" />

                  <div>
                    <p className="mb-1 fw-semibold">Annulation partielle — remboursement à 50 %.</p>

                    <p className="text-muted small mb-0">
                      La date d'annulation gratuite est dépassée. Vous pouvez encore annuler jusqu'à
                      48 h avant le départ.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger d-flex gap-2" role="alert">
                  <i className="bi bi-x-circle-fill text-danger flex-shrink-0 mt-1" />

                  <div>
                    <p className="mb-1 fw-semibold">Annulation non remboursable.</p>

                    <p className="text-muted small mb-0">
                      La location commence dans moins de 48 h. Aucun remboursement ne sera effectué
                      en cas d'annulation.
                    </p>
                  </div>
                </div>
              )}

              {/* ==================================================
                  STEP 1 : INSURANCE
              ================================================== */}

              {step === 'plan' && (
                <>
                  <p className="h4 mb-1">
                    <strong>Plan de protection</strong>
                  </p>

                  <p className="text-muted small mb-3">
                    Choisissez le niveau de protection adapté à votre voyage.
                  </p>

                  {planError && <div className="alert alert-danger py-2 small">{planError}</div>}

                  {/* ==================================================
                      NO INSURANCE
                  ================================================== */}

                  <div
                    className={`card mb-3 ${
                      selectedPlanId === 'none' ? 'border-primary border-2' : ''
                    }`}
                    onClick={() => handleSelectPlan('none')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body d-flex align-items-center gap-3">
                      <RadioDot selected={selectedPlanId === 'none'} />

                      <div className="flex-grow-1">
                        <h6 className="mb-0">
                          <strong>Sans assurance</strong>
                        </h6>

                        <p className="text-muted small mb-0">
                          Vous n'êtes pas couvert en cas de dommages.
                        </p>
                      </div>

                      <span className="fw-semibold text-muted">Gratuit</span>
                    </div>
                  </div>

                  {/* ==================================================
                      INSURANCE PLANS
                  ================================================== */}

                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className={`card mb-3 ${
                        selectedPlanId === plan.id ? 'border-primary border-2' : ''
                      }`}
                      onClick={() => handleSelectPlan(plan.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <RadioDot selected={selectedPlanId === plan.id} />

                          <h6 className="mb-0 flex-grow-1">
                            <strong>{plan.name}</strong>
                          </h6>

                          <div className="text-end">
                            <span className="fw-bold text-primary">
                              {(Number(plan.pricePerDay || 0) * nombreJours).toFixed(2)} €
                            </span>

                            <span className="text-muted small d-block">
                              {plan.pricePerDay} €/jour
                            </span>
                          </div>
                        </div>

                        {plan.description && (
                          <p className="text-muted small mb-2">{plan.description}</p>
                        )}

                        <ul className="list-unstyled small mb-0">
                          {plan.franchise != null && (
                            <li>
                              <i className="bi bi-shield-exclamation text-warning me-1" />
                              Franchise : <strong>{plan.franchise} €</strong>
                            </li>
                          )}

                          {plan.maxCoverageAmount && (
                            <li>
                              <i className="bi bi-shield-check text-success me-1" />
                              Couverture jusqu'à{' '}
                              <strong>
                                {Number(plan.maxCoverageAmount).toLocaleString('fr-FR')} €
                              </strong>
                            </li>
                          )}

                          {Array.isArray(plan.coverageDetails) &&
                            plan.coverageDetails.map((detail, index) => (
                              <li key={index}>
                                <i className="bi bi-check-circle-fill text-success me-1" />

                                {detail}
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  ))}

                  <button
                    className="btn btn-primary w-100 mt-2"
                    disabled={selectedPlanId === null || planLoading}
                    onClick={handleContinueToPayment}
                  >
                    {planLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Chargement…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-credit-card me-2" />
                        Continuer vers le paiement
                      </>
                    )}
                  </button>
                </>
              )}

              {/* ==================================================
                  STEP 2 : PAYMENT
              ================================================== */}

              {step === 'payment' && (
                <>
                  <button
                    className="btn btn-link p-0 mb-3 text-decoration-none"
                    onClick={() => setStep('plan')}
                  >
                    <i className="bi bi-arrow-left me-1" />
                    Modifier le plan
                  </button>

                  <div className="mb-3">
                    <p className="h5">Mode de paiement</p>

                    <p className="text-muted small">
                      <i className="bi bi-shield-lock-fill me-1" />
                      Vos informations seront stockées en toute sécurité.
                    </p>

                    {clientSecret ? (
                      <Elements options={options} stripe={stripePromise}>
                        <CheckoutForm VehiculeData={vehicle} />
                      </Elements>
                    ) : (
                      <div className="text-center py-4">
                        <span className="spinner-border text-primary" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ==================================================
              RIGHT COLUMN : SUMMARY
          ================================================== */}

          <div className="col-md-4">
            <div className="border rounded-3 p-3">
              {/* ==================================================
                  VEHICLE IMAGE
              ================================================== */}

              {vehicle?.principalPhotos && (
                <img
                  className="mb-3 w-100"
                  src={vehicle.principalPhotos}
                  alt="véhicule"
                  style={{
                    borderRadius: 12,
                    objectFit: 'cover',
                    maxHeight: 160,
                  }}
                />
              )}

              {/* ==================================================
                  HOST
              ================================================== */}

              <p className="small text-muted mb-0">
                {host?.firstName} {host?.lastName}
              </p>

              {/* ==================================================
                  VEHICLE
              ================================================== */}

              <p className="h5 mb-1">
                <strong>
                  {vehicleModel?.marque} {vehicleModel?.modele}
                </strong>
              </p>

              <p className="small mb-3">
                5.0 <i className="bi bi-star-fill text-warning" /> (5 Voyages)
              </p>

              {/* ==================================================
                  DATES
              ================================================== */}

              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="fw-bold mb-0">
                    {startMoment ? startMoment.format('DD MMM YY') : '-'}
                  </p>

                  <p className="text-muted small mb-0">
                    {startMoment ? startMoment.format('HH:mm') : '-'}
                  </p>
                </div>

                <i className="bi bi-arrow-right text-muted mt-1" />

                <div className="text-end">
                  <p className="fw-bold mb-0">{endMoment ? endMoment.format('DD MMM YY') : '-'}</p>

                  <p className="text-muted small mb-0">
                    {endMoment ? endMoment.format('HH:mm') : '-'}
                  </p>
                </div>
              </div>

              <hr />

              {/* ==================================================
                  LOCATION
              ================================================== */}

              <p className="fw-bold mb-1">Lieu de rencontre</p>

              <p className="small mb-3">
                <i className="bi bi-geo-alt-fill text-primary me-1" />

                {deliveryLocation?.addressEmplacementDelivery ||
                  reservation?.startEmplacement ||
                  annonce?.locationAddress ||
                  'Lieu non renseigné'}
              </p>

              <hr />

              {/* ==================================================
                  PRICING
              ================================================== */}

              <dl className="row small mb-2">
                <dt className="col-7">
                  {pricePerDay.toFixed(2)} € × {nombreJours} jour
                  {nombreJours > 1 ? 's' : ''}
                </dt>

                <dd className="col-5 text-end">{rentalSubtotal.toFixed(2)} €</dd>

                {/* Distance incluse */}

                {freeMinDistance != null && (
                  <>
                    <dt className="col-7">{freeMinDistance} km inclus</dt>

                    <dd className="col-5 text-end text-success">Gratuit</dd>
                  </>
                )}

                {/* Assurance */}

                {insuranceFee > 0 && (
                  <>
                    <dt className="col-7">Assurance</dt>

                    <dd className="col-5 text-end text-primary">+{insuranceFee.toFixed(2)} €</dd>
                  </>
                )}
              </dl>

              {/* ==================================================
                  TOTAL
              ================================================== */}

              <div className="d-flex justify-content-between align-items-center border-top pt-2">
                <span className="fw-bold">Total</span>

                <span className="fw-bold h5 mb-0 text-primary">{displayedTotal.toFixed(2)} €</span>
              </div>

              <p className="text-muted small mt-2 mb-0">
                Le montant final est recalculé et vérifié par le serveur au moment du paiement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default CheckoutPage;
